import React, { useRef } from "react";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { selectStyles } from "../../utils";
import { useAsyncSelect } from "../../hooks/useAsyncSelect";

/**
 * Searchable async dropdown — options load from API on type; selected value(s) hydrated by id.
 * Pass `creatable` to also allow adding a value that isn't in the option list (select2-style
 * "search existing or add new" in one field) — used for tag-like multi-selects, not just id lookups.
 */
export default function AsyncSearchSelect({
  fetchOptions,
  value,
  onChange,
  isMulti = false,
  isClearable = true,
  isDisabled = false,
  placeholder = "Type to search…",
  classNamePrefix = "ns-async-select",
  inputId,
  instanceId,
  enabled = true,
  styles,
  menuPortalTarget,
  menuPosition = "fixed",
  menuPlacement = "bottom",
  cacheOptions = true,
  extraOptions = [],
  creatable = false,
  formatCreateLabel,
  isValidNewOption,
}) {
  const { loadOptions, resolvedValue, defaultOptions, isLoading } = useAsyncSelect({
    fetchOptions,
    value,
    isMulti,
    enabled,
  });

  // Tracks whether the real default (search="") list has actually been fetched. Checking
  // mergedDefaultOptions.length instead is wrong: after a saved value is hydrated by id,
  // defaultOptions already has that ONE option in it, which falsely looks like "default list
  // already loaded" and skips the fetch - leaving only the selected option visible on open,
  // and nothing restores the full list when the search box is cleared back to blank either.
  const defaultListFetchedRef = useRef(false);

  const mergedDefaultOptions = React.useMemo(
    () => {
      const base = [...(extraOptions || []), ...(defaultOptions || [])];
      const map = new Map();
      base.forEach((o) => {
        if (o?.value != null && o.value !== "") map.set(String(o.value), o);
      });
      return [...map.values()];
    },
    [defaultOptions, extraOptions]
  );

  const Component = creatable ? AsyncCreatableSelect : AsyncSelect;

  return (
    <Component
      inputId={inputId}
      instanceId={instanceId}
      classNamePrefix={classNamePrefix}
      isMulti={isMulti}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      cacheOptions={cacheOptions}
      defaultOptions={mergedDefaultOptions}
      loadOptions={loadOptions}
      value={resolvedValue}
      onChange={onChange}
      placeholder={placeholder}
      styles={styles || selectStyles}
      menuPortalTarget={menuPortalTarget ?? (typeof document !== "undefined" ? document.body : null)}
      menuPosition={menuPosition}
      menuPlacement={menuPlacement}
      onMenuOpen={() => {
        if (!defaultListFetchedRef.current) {
          defaultListFetchedRef.current = true;
          loadOptions("");
        }
      }}
      {...(creatable ? { formatCreateLabel, isValidNewOption } : {})}
    />
  );
}
